import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'

interface DeleteFlowModalProps {
  canDirectDelete: boolean // platform owner or project owner
  canRequestDelete: boolean // platform owner or project owner or project admin
  onSuccess?: () => void
}

export interface DeleteFlowModalRef {
  open: (flow: any) => void
  close: () => void
}

const DeleteFlowModal = forwardRef<DeleteFlowModalRef, DeleteFlowModalProps>(
  ({ canDirectDelete, canRequestDelete, onSuccess }, ref) => {
    const { t } = useTranslation('pipeline-studio')
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [flow, setFlow] = useState<any>(null)
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')

    useImperativeHandle(ref, () => ({
      open: (flowData: any) => {
        setFlow(flowData)
        // If can direct delete, default to direct; otherwise request
        setActionType(canDirectDelete ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
      },
      close: () => {
        setIsOpen(false)
        setFlow(null)
        setActionType('direct')
        setError('')
        setSuccess(false)
      },
    }))

    const handleSubmit = async () => {
      if (!flow || loading) return

      const namespace = flow.metadata?.namespace
      const name = flow.metadata?.name

      if (!namespace || !name) {
        setError(t('deleteModal.error.invalidFlow', 'Invalid flow data'))
        return
      }

      try {
        setLoading(true)
        setError('')

        if (actionType === 'direct') {
          // Direct delete flow
          await k8sResourceService.deleteFlow(namespace, name)
        } else {
          // Submit request
          await k8sResourceService.requestDeleteFlow(namespace, name)
        }

        // Show success message instead of closing immediately
        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setFlow(null)
      setActionType('direct')
      setError('')
      setSuccess(false)

      // Only call onSuccess if the operation was successful
      if (wasSuccess) {
        onSuccess?.()
      }
    }

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Ready':
          return 'bg-green-100 text-green-800 border-green-200'
        case 'Progressing':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        case 'Error':
          return 'bg-red-100 text-red-800 border-red-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    if (!isOpen || !flow) return null

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-red-600">
                  {t('deleteModal.title', 'Delete Flow')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('deleteModal.subtitle', 'This action cannot be undone')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('deleteModal.success.directTitle', 'Flow deleted!')
                        : t('deleteModal.success.requestTitle', 'Delete request submitted!')}
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('deleteModal.success.directMessage', 'The flow has been removed from the system.')
                        : t('deleteModal.success.requestMessage', 'Request is pending approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Warning Message */}
            {!success && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900 mb-1">
                      {actionType === 'direct'
                        ? t('deleteModal.warning.directTitle', 'Are you sure you want to delete this flow?')
                        : t('deleteModal.warning.requestTitle', 'Are you sure you want to submit a delete request?')}
                    </p>
                    <p className="text-xs text-red-700">
                      {actionType === 'direct'
                        ? t('deleteModal.warning.directMessage', 'This action will take effect immediately and cannot be undone.')
                        : t('deleteModal.warning.requestMessage', 'Request will require project owner approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Flow Info */}
            {!success && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('deleteModal.flowInfo.title', 'Flow to be deleted:')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('deleteModal.flowInfo.name', 'Name')}:</span>
                    <span className="text-sm text-gray-900">{flow.metadata?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('deleteModal.flowInfo.namespace', 'Namespace')}:</span>
                    <span className="text-sm text-gray-900">{flow.metadata?.namespace}</span>
                  </div>
                  {flow.status?.phase && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{t('deleteModal.flowInfo.status', 'Status')}:</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(flow.status.phase)}`}>
                        {flow.status.phase}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Type Selection (Only if can do both) */}
            {!success && canDirectDelete && canRequestDelete && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('deleteModal.actionType.label', 'Action Type')}
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('deleteModal.actionType.direct', 'Direct Delete')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('deleteModal.actionType.directDesc', 'Delete flow immediately')}
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('deleteModal.actionType.request', 'Submit Request')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('deleteModal.actionType.requestDesc', 'Create delete request')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin/Non-owner Info Notice */}
            {!success && !canDirectDelete && canRequestDelete && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('deleteModal.notice.requestOnly', 'You can submit a delete flow request. The request will require project owner approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('deleteModal.buttons.close', 'Close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  {t('cancel', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={loading}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('deleteModal.buttons.processing', 'Processing')
                      : actionType === 'direct'
                        ? t('deleteModal.buttons.delete', 'Delete Flow')
                        : t('deleteModal.buttons.submitRequest', 'Submit Request')}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

DeleteFlowModal.displayName = 'DeleteFlowModal'

export default DeleteFlowModal
